import React, { useState } from 'react';
import { StanButton, StanCard } from '@/components';
import { useUser } from '@/queries/useUser';
import { useIntegrations } from '@/queries/useIntegrations';
import { useOAuthIntegrationCallback } from '../hooks/useOAuthIntegrationCallback';
import { useIntegrationsWithStatus } from '../hooks/useIntegrationsWithStatus';
import { useIntegrationActions } from '../hooks/useIntegrationActions';
import { IntegrationCard } from './integrations/IntegrationCard';
import { GoogleCalendarSettingsModal } from './integrations/GoogleCalendarSettingsModal';
import type { Integration } from '../types/integrations.types';

export const IntegrationsTab: React.FC = () => {
    const { user } = useUser();
    const { data: userIntegrations, refetchIntegrations } = useIntegrations();
    const [isCalendarSettingsOpen, setIsCalendarSettingsOpen] = useState(false);

    // Check for successful OAuth integration on mount (user returning from OAuth flow)
    useOAuthIntegrationCallback(userIntegrations);

    // Get integrations with their current status from backend
    const integrationsWithStatus = useIntegrationsWithStatus(userIntegrations);

    // Get connect/disconnect handlers
    const { loadingIntegrations, handleConnect, handleDisconnect } = useIntegrationActions({
        userIntegrations,
        refetchIntegrations,
    });

    const handleRequestIntegration = () => {
        const surveyUrl = `https://stanforcreators.typeform.com/to/eh9DMZ1s#user_id=${user?.user_id || ''}`;
        window.open(surveyUrl, '_blank');
    };

    const handleSettings = (integration: Integration) => {
        if (integration.id === 'google-calendar') {
            setIsCalendarSettingsOpen(true);
        }
    };

    return (
        <>
            <div className='space-y-8'>
                <div className='grid max-w-screen-2xl grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3'>
                    {integrationsWithStatus.map(integration => (
                        <IntegrationCard
                            key={integration.id}
                            integration={integration}
                            loading={loadingIntegrations.has(integration.id)}
                            onConnect={handleConnect}
                            onDisconnect={handleDisconnect}
                            onSettings={handleSettings}
                        />
                    ))}
                    <StanCard className='flex min-h-50 w-full flex-col items-center justify-center gap-6'>
                        <div className='text-heading-h4 mx-auto w-full text-center'>Don&apos;t see an integration?</div>

                        <div className='flex w-full justify-center'>
                            <StanButton
                                className='mx-auto w-fit'
                                size='md'
                                name='request-integration-button'
                                onClick={handleRequestIntegration}
                                disabled={loadingIntegrations.size > 0}
                            >
                                Request New Integration
                            </StanButton>
                        </div>
                    </StanCard>
                </div>
            </div>

            <GoogleCalendarSettingsModal
                isOpen={isCalendarSettingsOpen}
                onClose={() => setIsCalendarSettingsOpen(false)}
                userIntegrations={userIntegrations}
                refetchIntegrations={refetchIntegrations}
            />
        </>
    );
};

export default IntegrationsTab;
